import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronRight, X } from 'lucide-react';
import { setupApi } from '../api';
import './SetupChecklist.css';

const hiddenKey = (businessId) => `setup-hidden-${businessId}`;

const readHidden = (businessId) => {
  try {
    return localStorage.getItem(hiddenKey(businessId)) === '1';
  } catch {
    return false;
  }
};

// Getting-started steps on the Dashboard for admins, until they're all done (or hidden)
export function SetupChecklist({ businessId }) {
  const [status, setStatus] = useState(null);
  const [hidden, setHidden] = useState(() => readHidden(businessId));

  useEffect(() => {
    if (hidden) return;
    setupApi.status().then(setStatus).catch(() => setStatus(null));
  }, [hidden]);

  if (hidden || !status) return null;

  const shelf = status.first_shelf_id ? `/storage/${status.first_shelf_id}` : '/storage';
  const steps = [
    { done: status.store_connected, title: 'Connect your store', text: 'New orders then arrive by themselves.', to: '/settings' },
    { done: status.shelves > 0, title: 'Add a photo of a shelf', text: 'One photo per shelf or rack.', to: '/storage' },
    { done: status.bins > 0, title: 'Mark the bins', text: 'Auto detect finds them for you.', to: shelf },
    { done: status.products > 0, title: 'Import your products', text: 'Names, SKUs and photos from your store.', to: `${shelf}?tab=products` },
    { done: status.placed > 0, title: 'Put products into bins', text: 'Drag each product onto its bin.', to: `${shelf}?tab=products` },
    { done: status.completed_orders > 0, title: 'Pick your first order', text: 'The bins light up on the photo.', to: '/orders' },
  ];
  const doneCount = steps.filter(s => s.done).length;
  if (doneCount === steps.length) return null;
  const next = steps.find(s => !s.done);

  const hide = () => {
    try {
      localStorage.setItem(hiddenKey(businessId), '1');
    } catch {
      // Private mode: hidden for this visit only
    }
    setHidden(true);
  };

  return (
    <section className="card setup" aria-labelledby="setup-title">
      <div className="setup-header">
        <div>
          <h2 id="setup-title" className="setup-title">Get set up</h2>
          <p className="setup-progress-text">{doneCount} of {steps.length} done</p>
        </div>
        <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={hide} aria-label="Hide the setup steps" title="Hide">
          <X size={16} aria-hidden="true" />
        </button>
      </div>
      <div className="setup-progress" aria-hidden="true">
        <span style={{ width: `${(doneCount / steps.length) * 100}%` }} />
      </div>
      <ol className="setup-steps">
        {steps.map((step, index) => (
          <li key={step.title} className={`${step.done ? 'is-done' : ''} ${step === next ? 'is-next' : ''}`}>
            <Link to={step.to} className="setup-step">
              <span className="setup-step-mark" aria-hidden="true">{step.done ? <Check size={14} /> : index + 1}</span>
              <span className="setup-step-text">
                <span className="setup-step-title">{step.title}</span>
                {!step.done && <span className="setup-step-hint">{step.text}</span>}
              </span>
              {!step.done && <ChevronRight size={16} aria-hidden="true" className="setup-step-go" />}
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}
